import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ShieldCheck, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const VerifyEmailPage = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);
  const { verifyEmail, resendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const redirectTo = location.state?.redirectTo || '/browse';

  useEffect(() => {
    if (!email) navigate('/register');
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown > 0) {
      const t = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [cooldown]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) { toast.error('Please enter the full 6-digit code'); return; }
    setLoading(true);
    try {
      await verifyEmail(email, fullCode);
      toast.success('Email verified!');
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await resendCode(email);
      toast.success('New code sent!');
      setCooldown(60);
      setCode(['', '', '', '', '', '']);
    } catch (err) {
      toast.error(err.message || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (code.every(d => d !== '')) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (!email) return null;

  return (
    <div className="min-h-screen bg-[#030014] flex items-center justify-center px-4" data-testid="verify-email-page">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1565590915406-9c386cc9391d?w=1920)' }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/80 to-[#030014]/60" />
      </div>
      <Link to="/" className="absolute top-6 left-6 z-10">
        <img src="/reframes-logo.png" alt="reframes" className="h-9 md:h-11" />
      </Link>

      <div className="relative z-10 w-full max-w-md">
        <div className="admin-card p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-[#8B5CF6]/20 flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-[#8B5CF6]" />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Verify Your Email
          </h1>
          <p className="text-gray-400 text-sm mb-2">
            We sent a 6-digit code to
          </p>
          <p className="text-white font-medium mb-6" data-testid="verify-email-address">{email}</p>

          <form onSubmit={handleSubmit}>
            <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  className="w-12 h-14 text-center text-xl font-bold bg-[#030014] border-2 border-white/10 rounded-xl text-white focus:border-[#8B5CF6] focus:outline-none transition-colors"
                  data-testid={`code-input-${i}`}
                />
              ))}
            </div>

            <Button
              type="submit"
              disabled={loading || code.some(d => d === '')}
              className="w-full btn-primary h-12"
              data-testid="verify-submit-btn"
            >
              {loading ? <div className="spinner w-5 h-5" /> : 'Verify Email'}
            </Button>
          </form>

          <div className="mt-6">
            <button
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              className="flex items-center gap-2 mx-auto text-sm text-gray-400 hover:text-[#8B5CF6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="resend-code-btn"
            >
              <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-4">Code expires in 10 minutes</p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
